import React, { useState, useEffect, useCallback } from 'react';
import { useApi, useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Mail, Send, CheckCircle, AlertTriangle, Clock, Loader2, Inbox, Copy, Plus, Ban } from 'lucide-react';
import { RegistryLink } from '@/components/RegistryLink';
import { EmailGuardCard } from '@/components/EmailGuardCard';

const STATUS_COLORS = {
  processed: { bg: 'rgba(29,158,117,0.1)', text: '#1D9E75' },
  needs_review: { bg: 'rgba(232,160,32,0.1)', text: '#E8A020' },
  failed: { bg: 'rgba(226,75,74,0.1)', text: '#E24B4A' },
};

function copyToClipboard(text) {
  if (navigator.clipboard?.writeText) { navigator.clipboard.writeText(text).catch(() => {}); }
}

function EmailDomainCard({ api }) {
  const [cfg, setCfg] = useState(null);
  const [domain, setDomain] = useState('underaiter.com');
  const [senderLocal, setSenderLocal] = useState('claims');
  const [testTo, setTestTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState('');

  const load = useCallback(() => {
    api.get('/email/domain').then(r => setCfg(r.data)).catch(() => {});
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, okMsg) => {
    setBusy(true); setErr(''); setMsg('');
    try { const r = await fn(); setCfg(r.data); if (okMsg) setMsg(okMsg); }
    catch (e) { setErr(e?.response?.data?.detail || 'Request failed'); }
    setBusy(false);
  };

  const registerDomain = () => run(() => api.post('/email/domain', { domain, sender_local: senderLocal }), 'Domain registered — add the DNS records below, then Verify.');
  const verify = () => run(() => api.post('/email/domain/verify'), '');
  const refresh = () => run(() => api.post('/email/domain/refresh'), '');
  const removeDomain = async () => {
    setBusy(true); setErr('');
    try { await api.delete('/email/domain'); load(); } catch (e) { setErr(e?.response?.data?.detail || 'Remove failed'); }
    setBusy(false);
  };
  const sendTest = async () => {
    setBusy(true); setErr(''); setMsg('');
    try { const r = await api.post('/email/domain/test-send', { to: testTo }); setMsg(`Test sent from ${r.data.from} (id ${r.data.email_id})`); }
    catch (e) { setErr(e?.response?.data?.detail || 'Test send failed'); }
    setBusy(false);
  };
  const doCopy = (text, key) => { copyToClipboard(text); setCopied(key); setTimeout(() => setCopied(''), 1500); };

  if (!cfg) return null;
  const status = cfg.status || 'not_registered';
  const chip = {
    verified: { bg: 'rgba(29,158,117,0.1)', text: '#1D9E75', label: 'verified — sender live' },
    pending: { bg: 'rgba(232,160,32,0.1)', text: '#E8A020', label: 'pending — DNS propagating' },
    not_started: { bg: 'rgba(136,150,168,0.15)', text: '#5C6B7A', label: 'DNS records not added yet' },
    failed: { bg: 'rgba(226,75,74,0.1)', text: '#E24B4A', label: 'verification failed' },
    temporary_failure: { bg: 'rgba(232,160,32,0.1)', text: '#E8A020', label: 'temporary failure — retrying' },
    not_registered: { bg: 'rgba(136,150,168,0.15)', text: '#5C6B7A', label: 'no sending domain' },
  }[status] || { bg: 'rgba(136,150,168,0.15)', text: '#5C6B7A', label: status };
  const verified = status === 'verified';

  return (
    <Card className="rounded-sm border-l-4 bg-white p-5 space-y-3" style={{ borderLeftColor: verified ? '#1D9E75' : '#E8A020' }} data-testid="email-domain-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Send className="w-4 h-4" style={{ color: '#1A4C7C' }} />
          <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: '#0D1B2A' }}>Sending Domain — outbound email verification</h3>
        </div>
        <span className="text-[10px] px-2 py-0.5 rounded-sm font-medium" data-testid="email-domain-status-chip"
          style={{ background: chip.bg, color: chip.text }}>{chip.label}</span>
      </div>
      <p className="text-xs" style={{ color: '#5C6B7A' }}>
        Verifying a sending domain lets claim acknowledgements, renewal notices and all platform email deliver from your own address —
        it arms the <b>Part F PF-01</b> promise (acknowledge a claim within 1 business day). Current sender: <span className="font-mono" data-testid="email-domain-sender">{cfg.sender}</span>
      </p>

      {!cfg.configured ? (
        <div className="flex flex-wrap items-center gap-2">
          <Input value={domain} onChange={e => setDomain(e.target.value)} placeholder="Sending domain, e.g. underaiter.com"
            className="rounded-sm text-xs h-8 w-64" data-testid="email-domain-input" />
          <div className="flex items-center gap-1">
            <Input value={senderLocal} onChange={e => setSenderLocal(e.target.value)} placeholder="claims"
              className="rounded-sm text-xs h-8 w-28" data-testid="email-sender-local-input" />
            <span className="text-xs font-mono" style={{ color: '#5C6B7A' }}>@{domain.replace(/^www\./, '') || 'domain'}</span>
          </div>
          <Button size="sm" onClick={registerDomain} disabled={busy || !domain.trim()} className="rounded-sm text-xs" style={{ background: '#0D1B2A' }} data-testid="email-domain-register-btn">
            Register domain
          </Button>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <span className="font-mono px-2 py-0.5 rounded-sm" style={{ background: '#F5F7FA', color: '#0D1B2A' }} data-testid="email-domain-name">{cfg.domain}</span>
            {!verified && (
              <Button size="sm" onClick={verify} disabled={busy} className="rounded-sm text-xs h-7" style={{ background: '#1D9E75' }} data-testid="email-domain-verify-btn">
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Verify now'}
              </Button>
            )}
            <button onClick={refresh} className="text-[11px] underline" style={{ color: '#1A4C7C' }} data-testid="email-domain-refresh-btn">refresh status</button>
            <button onClick={removeDomain} className="text-[11px] underline ml-auto" style={{ color: '#E24B4A' }} data-testid="email-domain-remove-btn">remove</button>
          </div>

          {(cfg.records || []).length > 0 && !verified && (
            <table className="w-full text-[11px]" data-testid="email-domain-records">
              <thead><tr className="text-left text-[10px] uppercase tracking-wider" style={{ color: '#5C6B7A' }}>
                <th className="py-1 pr-2">Record</th><th className="py-1 pr-2">Type</th><th className="py-1 pr-2">Name</th><th className="py-1 pr-2">Value</th><th className="py-1">Status</th></tr></thead>
              <tbody>
                {cfg.records.map((r, i) => (
                  <tr key={i} style={{ borderTop: '1px solid rgba(136,150,168,0.12)' }} data-testid={`dns-record-${i}`}>
                    <td className="py-1.5 pr-2 font-semibold" style={{ color: '#0D1B2A' }}>{r.record}</td>
                    <td className="py-1.5 pr-2 font-mono" style={{ color: '#5C6B7A' }}>{r.type}</td>
                    <td className="py-1.5 pr-2 font-mono" style={{ color: '#0D1B2A' }}>{r.name}
                      <button onClick={() => doCopy(r.name, `n${i}`)} className="ml-1 underline" style={{ color: '#1A4C7C' }}>{copied === `n${i}` ? 'copied!' : 'copy'}</button>
                    </td>
                    <td className="py-1.5 pr-2 font-mono break-all" style={{ color: '#0D1B2A', maxWidth: '260px' }}>{(r.value || '').slice(0, 48)}{(r.value || '').length > 48 ? '…' : ''}
                      <button onClick={() => doCopy(r.value, `v${i}`)} className="ml-1 underline" style={{ color: '#1A4C7C' }}>{copied === `v${i}` ? 'copied!' : 'copy'}</button>
                    </td>
                    <td className="py-1.5" style={{ color: r.status === 'verified' ? '#1D9E75' : '#E8A020' }}>{r.status || 'not checked'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {verified && (
            <div className="p-2.5 rounded-sm text-[11px]" style={{ background: 'rgba(29,158,117,0.08)', color: '#14624A' }} data-testid="pf01-armed-note">
              <CheckCircle className="w-3 h-3 inline mr-1" style={{ color: '#1D9E75' }} />
              Domain verified {cfg.verified_at ? `on ${cfg.verified_at.slice(0, 10)}` : ''} — outbound email now sends from <b>{cfg.sender}</b> and <b>Part F PF-01 is WIRED</b>: the FNOL acknowledgement email genuinely reaches brokers.
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 pt-1" style={{ borderTop: '1px solid rgba(136,150,168,0.12)' }}>
            <Input value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="Send a test email to…"
              className="rounded-sm text-xs h-8 w-64 mt-2" data-testid="email-test-to-input" />
            <Button size="sm" onClick={sendTest} disabled={busy || !testTo.includes('@')} className="rounded-sm text-xs mt-2" style={{ background: '#00B4D8', color: '#0D1B2A', fontWeight: 600 }} data-testid="email-test-send-btn">
              <Send className="w-3 h-3 mr-1" /> Send test
            </Button>
          </div>
        </>
      )}
      {msg && <p className="text-xs" style={{ color: '#1D9E75' }} data-testid="email-domain-msg">{msg}</p>}
      {err && <p className="text-xs" style={{ color: '#E24B4A' }} data-testid="email-domain-error">{err}</p>}
    </Card>
  );
}

function MailboxConnectorCard({ api }) {
  const [status, setStatus] = useState(null);
  const [domain, setDomain] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState('');

  const load = useCallback(() => {
    api.get('/intake/connector').then(r => { setStatus(r.data); setDomain(r.data.inbound_domain || ''); }).catch(() => {});
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const saveDomain = async () => {
    setBusy(true); setErr('');
    try { await api.put('/intake/connector', { inbound_domain: domain }); load(); }
    catch (e) { setErr(e?.response?.data?.detail || 'Save failed'); }
    setBusy(false);
  };

  const createAddress = async () => {
    setBusy(true); setErr('');
    try { await api.post('/intake/addresses', { label }); setLabel(''); load(); }
    catch (e) { setErr(e?.response?.data?.detail || 'Could not create address'); }
    setBusy(false);
  };

  const deactivate = async (id) => {
    try { await api.delete(`/intake/addresses/${id}`); load(); } catch { /* noop */ }
  };

  const doCopy = (text, key) => { copyToClipboard(text); setCopied(key); setTimeout(() => setCopied(''), 1500); };

  if (!status) return null;
  const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}/api/webhooks/resend/inbound`;
  const ready = status.webhook_secret_set && status.inbound_domain;
  return (
    <Card className="rounded-sm border-l-4 bg-white p-5 space-y-3" style={{ borderLeftColor: ready ? '#1D9E75' : '#E8A020' }} data-testid="mailbox-connector-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Inbox className="w-4 h-4" style={{ color: '#1A4C7C' }} />
          <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: '#0D1B2A' }}>Mailbox Connector — forwarding addresses</h3>
        </div>
        <span className="text-[10px] px-2 py-0.5 rounded-sm font-medium" data-testid="connector-status-chip"
          style={ready ? { background: 'rgba(29,158,117,0.1)', color: '#1D9E75' } : { background: 'rgba(232,160,32,0.1)', color: '#E8A020' }}>
          {ready ? 'connector ready' : 'setup required'}
        </span>
      </div>
      <p className="text-xs" style={{ color: '#5C6B7A' }}>
        Clients forward broker emails (attachments included) to a dedicated address — extraction, entity matching and triage run automatically. No migration, no software on their side.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <Input value={domain} onChange={e => setDomain(e.target.value)} placeholder="Inbound domain, e.g. inbound.underaiter.com"
          className="rounded-sm text-xs h-8 w-72" data-testid="connector-domain-input" />
        <Button size="sm" onClick={saveDomain} disabled={busy || !domain.trim()} className="rounded-sm text-xs" style={{ background: '#0D1B2A' }} data-testid="connector-domain-save">Save domain</Button>
        <button onClick={() => doCopy(webhookUrl, 'wh')} className="flex items-center gap-1 text-[11px] underline" style={{ color: '#1A4C7C' }} data-testid="connector-webhook-copy">
          <Copy size={11} /> {copied === 'wh' ? 'copied!' : 'copy webhook URL'}
        </button>
      </div>
      <div className="p-2.5 rounded-sm text-[11px]" style={{ background: '#FBF6E9', color: '#6B5D1F' }} data-testid="connector-setup-steps">
        <b>Go-live checklist (Resend dashboard):</b> 1) Emails → Receiving → add <span className="font-mono">{status.inbound_domain || 'your inbound subdomain'}</span> and create the MX record it shows · 2) Webhooks → add the URL above with event <span className="font-mono">email.received</span> · 3) Put the webhook's signing secret in the backend env as <span className="font-mono">RESEND_WEBHOOK_SECRET</span>{status.webhook_secret_set ? ' (a value is set — replace it with the real signing secret when you register the webhook)' : ' (not set)'} and restart.
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-1" style={{ borderTop: '1px solid rgba(136,150,168,0.12)' }}>
        <Input value={label} onChange={e => setLabel(e.target.value)} placeholder="Label — e.g. Harbourline UA / SME intake"
          className="rounded-sm text-xs h-8 w-72 mt-2" data-testid="address-label-input" />
        <Button size="sm" onClick={createAddress} disabled={busy || !status.inbound_domain} className="rounded-sm text-xs mt-2" style={{ background: '#00B4D8', color: '#0D1B2A', fontWeight: 600 }} data-testid="address-create-btn">
          <Plus className="w-3 h-3 mr-1" /> New forwarding address
        </Button>
      </div>
      {(status.addresses || []).filter(a => a.active).map(a => (
        <div key={a.address_id} className="flex flex-wrap items-center gap-2 py-1.5" style={{ borderTop: '1px solid rgba(136,150,168,0.08)' }} data-testid={`intake-address-${a.address_id}`}>
          <span className="font-mono text-xs px-2 py-0.5 rounded-sm" style={{ background: '#F5F7FA', color: '#0D1B2A' }}>{a.address}</span>
          <button onClick={() => doCopy(a.address, a.address_id)} className="text-[11px] underline" style={{ color: '#1A4C7C' }} data-testid={`address-copy-${a.address_id}`}>
            {copied === a.address_id ? 'copied!' : 'copy'}
          </button>
          <span className="text-[11px]" style={{ color: '#5C6B7A' }}>{a.label} · {a.received_count || 0} received</span>
          <button onClick={() => deactivate(a.address_id)} className="flex items-center gap-1 text-[11px] ml-auto" style={{ color: '#E24B4A' }} data-testid={`address-deactivate-${a.address_id}`}>
            <Ban size={11} /> deactivate
          </button>
        </div>
      ))}
      {err && <p className="text-xs" style={{ color: '#E24B4A' }} data-testid="connector-error">{err}</p>}
    </Card>
  );
}

function AddressAnalyticsCard({ api }) {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    api.get('/intake/analytics').then(r => setRows(r.data.addresses)).catch(() => setRows([]));
  }, [api]);

  if (!rows || rows.length === 0) return null;
  const withData = rows.filter(r => r.total > 0);
  const matchColor = (pct) => pct === null ? '#5C6B7A' : pct >= 60 ? '#1D9E75' : pct >= 30 ? '#E8A020' : '#E24B4A';
  return (
    <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white p-5" data-testid="address-analytics-card">
      <h3 className="text-sm font-semibold uppercase tracking-wider mb-1" style={{ color: '#0D1B2A' }}>Address Analytics — the connector earning its keep</h3>
      <p className="text-xs mb-3" style={{ color: '#5C6B7A' }}>Volume, entity-match rate and email-to-structured-submission turnaround, per forwarding address.</p>
      {withData.length === 0 ? (
        <p className="text-xs" style={{ color: '#5C6B7A' }} data-testid="analytics-empty">No inbound mail yet — numbers appear with the first forwarded email.</p>
      ) : (
        <table className="w-full text-xs" data-testid="address-analytics-table">
          <thead><tr className="text-left text-[10px] uppercase tracking-wider" style={{ color: '#5C6B7A' }}>
            <th className="py-1.5 pr-2">Address</th><th className="py-1.5 pr-2">Volume</th><th className="py-1.5 pr-2">Match rate</th>
            <th className="py-1.5 pr-2">Median turnaround</th><th className="py-1.5 pr-2">Outcomes</th><th className="py-1.5">8-week trend</th></tr></thead>
          <tbody>
            {withData.map(r => {
              const max = Math.max(...r.weekly, 1);
              return (
                <tr key={r.address_id} style={{ borderTop: '1px solid rgba(136,150,168,0.12)' }} data-testid={`analytics-row-${r.address_id}`}>
                  <td className="py-2 pr-2">
                    <span className="font-mono" style={{ color: '#0D1B2A' }}>{r.address}</span>
                    <p className="text-[10px]" style={{ color: '#5C6B7A' }}>{r.label}{!r.active && ' · deactivated'}</p>
                  </td>
                  <td className="py-2 pr-2" style={{ color: '#0D1B2A' }} data-testid={`analytics-volume-${r.address_id}`}>
                    <b>{r.last_30d}</b> <span style={{ color: '#5C6B7A' }}>in 30d · {r.total} all-time</span>
                  </td>
                  <td className="py-2 pr-2" data-testid={`analytics-match-${r.address_id}`}>
                    <b style={{ color: matchColor(r.match_rate_pct) }}>{r.match_rate_pct !== null ? `${r.match_rate_pct}%` : '—'}</b>
                    <span className="text-[10px] ml-1" style={{ color: '#5C6B7A' }}>matched to a known business</span>
                  </td>
                  <td className="py-2 pr-2 font-mono" style={{ color: '#0D1B2A' }} data-testid={`analytics-turnaround-${r.address_id}`}>
                    {r.median_turnaround_s !== null ? `${r.median_turnaround_s}s` : '—'}
                    <span className="text-[10px] font-sans ml-1" style={{ color: '#5C6B7A' }}>email → structured</span>
                  </td>
                  <td className="py-2 pr-2 text-[11px]" style={{ color: '#5C6B7A' }}>
                    <span style={{ color: '#1D9E75' }}>{r.processed} auto</span> · <span style={{ color: '#E8A020' }}>{r.needs_review} review</span>{r.failed > 0 && <> · <span style={{ color: '#E24B4A' }}>{r.failed} failed</span></>}
                  </td>
                  <td className="py-2">
                    <div className="flex items-end gap-0.5 h-6">
                      {r.weekly.map((v, i) => (
                        <div key={i} className="w-2 rounded-sm" style={{ height: `${Math.max(8, (v / max) * 100)}%`, background: v ? '#00B4D8' : '#E8EDF2' }} title={`${v} msg`} />
                      ))}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Card>
  );
}

export default function EmailPipelinePage() {
  const api = useApi();
  const { user } = useAuth();
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSimulator, setShowSimulator] = useState(false);
  const [sending, setSending] = useState(false);
  const [simForm, setSimForm] = useState({
    sender_email: 'broker@example.com',
    sender_name: 'John Smith',
    subject: 'New Submission - ABC Plumbing',
    body: `Hi,

Please find attached our submission for ABC Plumbing Pty Ltd.

ABN: 51 824 753 556
Business Name: ABC Plumbing Services Pty Ltd
Industry: Trades - Plumbing
Annual Turnover: $1,200,000
Employees: 6
Sum Insured: $800,000
Construction: Brick Veneer
No claims in the last 3 years.
WHS system in place.

Please provide a quote at your earliest convenience.

Regards,
John Smith
Smith Insurance Brokers`,
    attachments_text: '',
  });

  const loadEmails = useCallback(() => {
    api.get('/email/intake?limit=20').then(r => setEmails(r.data)).catch(err => { console.error('API error:', err?.message); }).finally(() => setLoading(false));
  }, [api]);

  useEffect(() => { loadEmails(); }, [loadEmails]);

  const handleSimulate = async () => {
    setSending(true);
    try {
      await api.post('/email/intake', simForm);
      setShowSimulator(false);
      loadEmails();
    } catch (err) { console.error("API error:", err?.message);
    }
    setSending(false);
  };

  const isAdmin = ['platform_owner', 'mga_admin'].includes(user?.role);

  if (loading) return <div className="space-y-3" data-testid="email-loading">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-20 rounded-sm" />)}</div>;

  return (
    <div className="space-y-6" data-testid="email-pipeline-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ color: '#0D1B2A', fontFamily: "'IBM Plex Sans', sans-serif" }}>Email Intake Pipeline</h1>
          <p className="text-sm mt-1" style={{ color: '#5C6B7A' }}>Automated submission processing from email</p>
        </div>
        {isAdmin && (
          <Button data-testid="simulate-email-btn" onClick={() => setShowSimulator(!showSimulator)}
            className="rounded-sm text-xs" style={{ background: '#1A4C7C' }}>
            <Send className="w-3.5 h-3.5 mr-2" /> Simulate Email
          </Button>
        )}
      </div>

      {/* Outbound guard — visible to all staff (read-only), changeable by admins only */}
      <EmailGuardCard />
      {isAdmin && <EmailDomainCard api={api} />}
      {isAdmin && <MailboxConnectorCard api={api} />}
      {isAdmin && <AddressAnalyticsCard api={api} />}

      {/* Email Simulator */}
      {showSimulator && (
        <Card className="rounded-sm border-l-4 bg-white p-6 space-y-4" style={{ borderLeftColor: '#00B4D8' }} data-testid="email-simulator">
          <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: '#0D1B2A' }}>Email Simulator</h3>
          <p className="text-xs" style={{ color: '#5C6B7A' }}>Simulate an incoming submission email. Fraime will extract fields using Gemini Flash and match the entity.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs" style={{ color: '#5C6B7A' }}>Sender Email</Label>
              <Input value={simForm.sender_email} onChange={e => setSimForm(p => ({...p, sender_email: e.target.value}))} className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs" style={{ color: '#5C6B7A' }}>Sender Name</Label>
              <Input value={simForm.sender_name} onChange={e => setSimForm(p => ({...p, sender_name: e.target.value}))} className="mt-1 rounded-sm" />
            </div>
          </div>
          <div>
            <Label className="text-xs" style={{ color: '#5C6B7A' }}>Subject</Label>
            <Input value={simForm.subject} onChange={e => setSimForm(p => ({...p, subject: e.target.value}))} className="mt-1 rounded-sm" />
          </div>
          <div>
            <Label className="text-xs" style={{ color: '#5C6B7A' }}>Email Body</Label>
            <textarea data-testid="email-body-input" value={simForm.body} onChange={e => setSimForm(p => ({...p, body: e.target.value}))}
              className="mt-1 w-full h-40 px-3 py-2 border border-input rounded-sm text-sm resize-none focus:ring-2 focus:ring-[#1A4C7C]" />
          </div>
          <Button data-testid="send-simulated-email" onClick={handleSimulate} disabled={sending}
            className="rounded-sm" style={{ background: '#1A4C7C' }}>
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            {sending ? 'Processing with Gemini Flash...' : 'Send Simulated Email'}
          </Button>
        </Card>
      )}

      {/* Email List */}
      <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
        <div className="p-4 border-b border-[rgba(136,150,168,0.2)]">
          <h2 className="text-sm font-semibold uppercase tracking-wider" style={{ color: '#0D1B2A' }}>
            Processed Emails ({emails.length})
          </h2>
        </div>
        {emails.length === 0 ? (
          <div className="p-8 text-center">
            <Mail className="w-8 h-8 mx-auto mb-3" style={{ color: '#5C6B7A' }} />
            <p style={{ color: '#5C6B7A' }}>No emails processed yet. Use the simulator to test the pipeline.</p>
          </div>
        ) : (
          <div className="divide-y divide-[rgba(136,150,168,0.1)]">
            {emails.map((email) => {
              const sc = STATUS_COLORS[email.status] || STATUS_COLORS.needs_review;
              const extracted = email.extracted_fields || {};
              return (
                <div key={email.email_id} data-testid={`email-${email.email_id}`} className="p-4 hover:bg-[#F5F7FA] transition-colors">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4" style={{ color: '#1A4C7C' }} />
                        <span className="font-medium text-sm" style={{ color: '#0D1B2A' }}>{email.sender_name || email.sender_email}</span>
                        <span className="px-2 py-0.5 rounded-sm text-[10px] font-semibold capitalize" style={{ background: sc.bg, color: sc.text }}>
                          {email.status?.replace('_', ' ')}
                        </span>
                      </div>
                      <p className="text-xs mt-1" style={{ color: '#5C6B7A' }}>{email.subject}</p>
                    </div>
                    <span className="text-xs" style={{ color: '#5C6B7A' }}>{new Date(email.created_at).toLocaleString()}</span>
                  </div>
                  
                  {/* Extracted fields */}
                  <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    {extracted.business_name && (
                      <span style={{ color: '#0D1B2A' }}>Business: <strong>{extracted.business_name}</strong></span>
                    )}
                    {extracted.abn && (
                      <span style={{ color: '#0D1B2A' }}>ABN: <RegistryLink abn={extracted.abn} className="font-semibold" /></span>
                    )}
                    {extracted.annual_turnover && (
                      <span style={{ color: '#0D1B2A' }}>Turnover: <strong>${Number(extracted.annual_turnover).toLocaleString()}</strong></span>
                    )}
                    {extracted.sum_insured && (
                      <span style={{ color: '#0D1B2A' }}>SI: <strong>${Number(extracted.sum_insured).toLocaleString()}</strong></span>
                    )}
                  </div>

                  {/* Entity match */}
                  <div className="mt-2 flex items-center gap-2 text-xs">
                    {email.entity_matched ? (
                      <>
                        <CheckCircle className="w-3 h-3" style={{ color: '#1D9E75' }} />
                        <span style={{ color: '#1D9E75' }}>Entity matched: {email.entity_name}</span>
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="w-3 h-3" style={{ color: '#E8A020' }} />
                        <span style={{ color: '#E8A020' }}>No entity match found</span>
                      </>
                    )}
                  </div>

                  {/* Missing fields */}
                  {email.missing_fields?.length > 0 && (
                    <div className="mt-2 text-xs" style={{ color: '#E8A020' }}>
                      Missing: {email.missing_fields.join(', ')}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
